import type { DragEvent, KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";
import type { Equation } from "@src/features/equation-editor/equation";
import type { Dimension } from "@src/math/types";
import type { MoveDirection } from "@src/features/equation-editor/item-ordering";
import type { Translate } from "@src/i18n/translate";

export type ReorderItemKind = "matrix" | "vector";

interface DragItem {
  kind: ReorderItemKind;
  id: string;
}

export interface DropTarget extends DragItem {
  side: "before" | "after";
}

interface ReorderActions {
  moveItem(
    kind: ReorderItemKind,
    id: string,
    direction: MoveDirection,
  ): number | null;
  moveItemTo(
    kind: ReorderItemKind,
    id: string,
    targetId: string,
    position: DropTarget["side"],
  ): void;
}

export function useReorder<D extends Dimension>({
  equation,
  actions,
  t,
}: {
  equation: Equation<D>;
  actions: ReorderActions;
  t: Translate;
}) {
  const [announcement, setAnnouncement] = useState("");
  const drag = useRef<DragItem | null>(null);
  const dragPreview = useRef<HTMLElement | null>(null);
  const [dragging, setDragging] = useState<DragItem | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  const endDrag = () => {
    drag.current = null;
    dragPreview.current?.remove();
    dragPreview.current = null;
    setDragging(null);
    setDropTarget(null);
  };

  useEffect(
    () => () => {
      dragPreview.current?.remove();
    },
    [],
  );

  const reorder = (
    kind: ReorderItemKind,
    id: string,
    direction: MoveDirection,
    element: HTMLElement,
  ) => {
    const newIndex = actions.moveItem(kind, id, direction);
    if (newIndex === null) return;
    const items = kind === "matrix" ? equation.matrices : equation.vectors;
    const movedItem = items.find((item) => item.id === id);
    if (!movedItem) throw new Error(`Missing reordered item: ${id}`);
    setAnnouncement(
      t("reorderedItem", {
        label: movedItem.label,
        position: newIndex + 1,
        total: items.length,
      }),
    );
    requestAnimationFrame(() => {
      element.focus();
    });
  };

  const keyboardReorder = (
    event: KeyboardEvent<HTMLElement>,
    kind: ReorderItemKind,
    id: string,
  ) => {
    if (!isKeyboardReorderEvent(event)) return;
    event.preventDefault();
    reorder(kind, id, event.key === "ArrowLeft" ? -1 : 1, event.currentTarget);
  };

  const startDrag = (
    kind: ReorderItemKind,
    id: string,
    preview?: HTMLElement,
  ) => {
    const dragItem = { kind, id };
    drag.current = dragItem;
    dragPreview.current = preview ?? null;
    setDragging(dragItem);
  };

  const dragOver = (
    event: DragEvent<HTMLElement>,
    kind: ReorderItemKind,
    targetId: string,
  ) => {
    event.preventDefault();
    const dragItem = drag.current;
    if (!dragItem || dragItem.kind !== kind || dragItem.id === targetId) {
      setDropTarget(null);
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    setDropTarget({
      kind,
      id: targetId,
      side: event.clientX < bounds.left + bounds.width / 2 ? "before" : "after",
    });
  };

  const drop = (
    event: DragEvent<HTMLElement>,
    kind: ReorderItemKind,
    targetId: string,
  ) => {
    event.preventDefault();
    const dragItem = drag.current;
    if (!dragItem || dragItem.kind !== kind || dragItem.id === targetId) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    actions.moveItemTo(
      kind,
      dragItem.id,
      targetId,
      event.clientX < bounds.left + bounds.width / 2 ? "before" : "after",
    );
    endDrag();
  };

  return {
    announcement,
    dragging,
    dropTarget,
    keyboardReorder,
    startDrag,
    dragOver,
    drop,
    endDrag,
  };
}

function isKeyboardReorderEvent(event: KeyboardEvent<HTMLElement>): boolean {
  return (
    event.target === event.currentTarget &&
    hasOnlyAltModifier(event) &&
    isHorizontalArrow(event.key)
  );
}

function hasOnlyAltModifier(event: KeyboardEvent<HTMLElement>): boolean {
  return event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
}

function isHorizontalArrow(key: string): boolean {
  return key === "ArrowLeft" || key === "ArrowRight";
}
